import React, { useState } from 'react';
import { CaseHeader } from '../components/CaseHeader';
import { SearchIcon, SendIcon, FileTextIcon, InfoIcon } from 'lucide-react';
export function AskPage() {
  const [query, setQuery] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setHasSearched(true);
    }
  };
  const exampleQueries = ['What did Defendant B say about notice?', 'Compare all parties on timeline of events', "What contradictions exist in Party A's responses?", 'Which responses refer to document production?'];
  return <div className="flex flex-col h-full w-full overflow-hidden bg-navy" data-id="element-188">
      <CaseHeader data-id="element-189" />

      <div className="flex-1 overflow-auto p-6 flex flex-col max-w-5xl mx-auto w-full" data-id="element-190">
        <div className="mb-8" data-id="element-191">
          <h2 className="text-xl font-semibold text-white mb-2" data-id="element-192">
            Ask the Discovery Record
          </h2>
          <p className="text-text-secondary text-sm" data-id="element-193">
            Query across all interrogatories, requests for production, and
            admissions.
          </p>
        </div>

        <div className="panel p-4 mb-8" data-id="element-194">
          <form onSubmit={handleSearch} className="flex flex-col gap-4" data-id="element-195">
            <div className="flex gap-4" data-id="element-196">
              <div className="flex-1 relative" data-id="element-197">
                <textarea value={query} onChange={e => setQuery(e.target.value)} placeholder="e.g., What did Blackstone say about water intrusion notice?" className="input-field min-h-[80px] resize-none pt-3 pb-3" onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSearch(e);
                }
              }} data-id="element-198" />
              </div>
              <button type="submit" className="btn-primary flex items-center gap-2 h-fit mt-auto" disabled={!query.trim()} data-id="element-199">
                <SearchIcon size={16} data-id="element-200" />
                Analyze
              </button>
            </div>

            <div className="flex items-center gap-4 border-t border-navy-border pt-3" data-id="element-201">
              <span className="text-xs font-medium text-text-muted" data-id="element-202">
                Scope:
              </span>
              <select className="bg-navy border border-navy-border rounded-sm px-2 py-1 text-text-secondary text-xs focus:outline-none focus:border-accent-amber" data-id="element-203">
                <option data-id="element-204">All Parties</option>
                <option data-id="element-205">Meridian Properties</option>
                <option data-id="element-206">Blackstone Dev.</option>
              </select>
              <select className="bg-navy border border-navy-border rounded-sm px-2 py-1 text-text-secondary text-xs focus:outline-none focus:border-accent-amber" data-id="element-207">
                <option data-id="element-208">All Sets</option>
                <option data-id="element-209">Interrogatories Only</option>
                <option data-id="element-210">RFPs Only</option>
              </select>
            </div>
          </form>
        </div>

        {!hasSearched ? <div className="grid grid-cols-2 gap-4 mt-4" data-id="element-211">
            {exampleQueries.map((ex, i) => <button key={i} onClick={() => {
          setQuery(ex);
          setHasSearched(true);
        }} className="panel p-4 text-left hover:border-accent-amber transition-colors flex items-start gap-3 group" data-id="element-212">
                <SearchIcon size={16} className="text-text-muted mt-0.5 group-hover:text-accent-amber transition-colors" data-id="element-213" />
                <span className="text-sm text-text-secondary group-hover:text-white transition-colors" data-id="element-214">
                  {ex}
                </span>
              </button>)}
          </div> : <div className="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-215">
            <div className="panel p-6 border-l-4 border-l-accent-amber" data-id="element-216">
              <h3 className="text-lg font-semibold text-white mb-4" data-id="element-217">
                Analysis: Water Intrusion Notice
              </h3>
              <div className="prose prose-invert prose-sm max-w-none text-text-secondary space-y-4" data-id="element-218">
                <p data-id="element-219">
                  Based on the discovery record, Blackstone Development Corp.
                  states they first received notice of potential water intrusion
                  on <strong data-id="element-220">October 12, 2023</strong> via an email from
                  Plaintiff's property manager.
                </p>
                <p data-id="element-221">
                  However, there appears to be a potential contradiction or
                  nuance regarding earlier awareness. In response to ROG 2
                  regarding foundation pouring communications, Blackstone refers
                  to daily site logs (BLK_001452 - BLK_002105) from June-July
                  2022. While they objected to the interrogatory as vague, those
                  logs may contain earlier references to moisture issues during
                  construction.
                </p>
              </div>
            </div>

            <div data-id="element-222">
              <h4 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-223">
                <FileTextIcon size={14} data-id="element-224" />
                Source Citations
              </h4>
              <div className="flex flex-col gap-3" data-id="element-225">
                <div className="panel p-4 hover:border-navy-border cursor-pointer border-l-2 border-l-transparent hover:border-l-accent-amber transition-all" data-id="element-226">
                  <div className="flex justify-between items-start mb-2" data-id="element-227">
                    <div className="flex items-center gap-2" data-id="element-228">
                      <span className="text-xs font-semibold text-white bg-navy-elevated px-2 py-1 rounded-sm border border-navy-border" data-id="element-229">
                        Plaintiff's First Set of Interrogatories to Blackstone
                      </span>
                      <span className="text-xs text-text-muted" data-id="element-230">ROG 3</span>
                    </div>
                    <span className="text-xs text-accent-amber" data-id="element-231">
                      Direct Match
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary mb-2 italic" data-id="element-232">
                    "State the date on which Blackstone Development Corp. first
                    became aware of water intrusion..."
                  </p>
                  <div className="bg-navy p-3 rounded-sm border border-navy-border text-sm text-white" data-id="element-233">
                    "Defendant first received notice of potential water
                    intrusion via an email from Plaintiff's property manager on
                    October 12, 2023."
                  </div>
                </div>

                <div className="panel p-4 hover:border-navy-border cursor-pointer border-l-2 border-l-transparent hover:border-l-accent-amber transition-all" data-id="element-234">
                  <div className="flex justify-between items-start mb-2" data-id="element-235">
                    <div className="flex items-center gap-2" data-id="element-236">
                      <span className="text-xs font-semibold text-white bg-navy-elevated px-2 py-1 rounded-sm border border-navy-border" data-id="element-237">
                        Plaintiff's First Set of Interrogatories to Blackstone
                      </span>
                      <span className="text-xs text-text-muted" data-id="element-238">ROG 2</span>
                    </div>
                    <span className="text-xs text-text-muted" data-id="element-239">
                      Related Context
                    </span>
                  </div>
                  <p className="text-sm text-text-secondary mb-2 italic" data-id="element-240">
                    "Describe in detail all communications... regarding the
                    foundation pouring..."
                  </p>
                  <div className="bg-navy p-3 rounded-sm border border-navy-border text-sm text-text-secondary" data-id="element-241">
                    "Objection. Vague and ambiguous... Subject to said
                    objection, Defendant refers Plaintiff to the daily site
                    logs... BLK_001452 through BLK_002105."
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}